/**
 * Metaobject-based Bundle System for Collection Pages
 * Handles bundle option rendering and cart addition for collection page products
 */

/**
 * Clean option name by removing identifier suffix (e.g., "| EK244M")
 * @param {string} optionName - Original option name (e.g., "Piston Ring Set | EK244")
 * @returns {string} Cleaned option name (e.g., "Piston Ring Set")
 */
function cleanOptionName(optionName) {
    if (!optionName) return optionName;
    return optionName.split('|')[0].trim();
}

/**
 * Convert string to handleized format for property names
 * @param {string} name - Name to handleize
 * @returns {string} Handleized name (e.g., "piston-ring-set")
 */
function handleize(name) {
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
}

/**
 * Sort bundle options in predefined order
 */
function sortBundleOptions(options) {
    const orderPriority = {
        'piston set': 1,
        'piston ring set': 2,
        'main bearing set': 3,
        'main bearings set': 3,
        'rod bearing set': 4
    };
    
    return options.slice().sort((a, b) => {
        const nameA = cleanOptionName(a.bundle_option_name || '').toLowerCase();
        const nameB = cleanOptionName(b.bundle_option_name || '').toLowerCase();
        
        let priorityA = 999;
        let priorityB = 999;
        
        for (const [key, priority] of Object.entries(orderPriority)) {
            if (nameA.includes(key)) priorityA = Math.min(priorityA, priority);
            if (nameB.includes(key)) priorityB = Math.min(priorityB, priority);
        }
        
        return priorityA - priorityB;
    });
}

window.collectionBundleManager = {
    initialized: new Set(),
    bundleCache: new Map(),

    /**
     * Initialize bundle products in collection container
     * @param {HTMLElement} container - Container element with product items
     * @param {string} context - Context ('collection', 'search', etc.)
     */
    async initializeCollectionProducts(container, context = 'collection') {
        // Look for product items with attr-prod-id attribute
        const productItems = container.querySelectorAll('[attr-prod-id]');
        
        for (const item of productItems) {
            const productId = item.getAttribute("attr-prod-id");
            const bundleData = window.collectionBundleData?.[productId];
            
            // Check if product is out of stock first
            const isOutOfStock = this.isProductOutOfStock(item);
            
            if (isOutOfStock) {
                // Product is out of stock - ensure bundle options are removed and buttons are correct
                const form = item.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
                if (form) {
                    const bundleOptions = form.querySelector(".bold_option_set");
                    if (bundleOptions) {
                        bundleOptions.remove();
                    }
                    
                    // Hide "Choose Size" button
                    const bundleChooseBtn = item.querySelector("#bundle-choose");
                    if (bundleChooseBtn) {
                        bundleChooseBtn.classList.add("hide-bundle-choose");
                        bundleChooseBtn.style.display = 'none';
                    }
                    
                    // Ensure "Out of stock" button is visible
                    const addToCartBtn = item.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
                    if (addToCartBtn && addToCartBtn.getAttribute("aria-label") === "Out of stock") {
                        addToCartBtn.classList.remove("hide-bundle-choose");
                        addToCartBtn.style.display = '';
                    }
                }
                continue; // Skip initialization for out-of-stock products
            }
            
            // Check if this product has metaobject bundle data
            if (bundleData && !this.initialized.has(productId)) {
                await this.initializeBundleProduct(item, productId, context);
                this.initialized.add(productId);
            } else if (bundleData && this.initialized.has(productId)) {
                // Already initialized, but ensure visibility is correct (in case DOM was modified)
                const form = item.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
                if (form) {
                    const bundleOptions = form.querySelector(".bold_option_set");
                    const bundleChooseBtn = item.querySelector("#bundle-choose");
                    const addToCartBtn = item.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
                    
                    if (bundleOptions) {
                        // Ensure bundle options are hidden initially
                        bundleOptions.classList.add("hide-bundle-choose");
                        
                        // Show "Choose Size" button
                        if (bundleChooseBtn) {
                            bundleChooseBtn.classList.remove("hide-bundle-choose");
                            bundleChooseBtn.style.display = '';
                        }
                        
                        // Hide "Add to Cart" button initially
                        if (addToCartBtn) {
                            addToCartBtn.classList.add("hide-bundle-choose");
                        }
                    }
                }
            }
        }
    },

    /**
     * Initialize bundle for a single product
     * @param {HTMLElement} productItemElement - Product item element
     * @param {string} productId - Product ID
     * @param {string} context - Context
     */
    async initializeBundleProduct(productItemElement, productId, context) {
        const bundleData = window.collectionBundleData?.[productId];
        const form = productItemElement.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");

        if (bundleData && form) {
            // Check if product is out of stock
            const isOutOfStock = this.isProductOutOfStock(productItemElement);
            
            if (isOutOfStock) {
                
                // Remove any bundle options that might have been injected
                const existingBundleOptions = form.querySelector(".bold_option_set");
                if (existingBundleOptions) {
                    existingBundleOptions.remove();
                }
                
                // Hide "Choose Size" button if it exists
                const bundleChooseBtn = productItemElement.querySelector("#bundle-choose");
                if (bundleChooseBtn) {
                    bundleChooseBtn.classList.add("hide-bundle-choose");
                    bundleChooseBtn.style.display = 'none';
                }
                
                // Ensure "Out of stock" button is visible (remove hide-bundle-choose class)
                const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
                if (addToCartBtn && addToCartBtn.getAttribute("aria-label") === "Out of stock") {
                    addToCartBtn.classList.remove("hide-bundle-choose");
                    addToCartBtn.style.display = '';
                }
                
                return; // Don't initialize bundle options for out-of-stock products
            }
            
            this.bundleCache.set(productId, bundleData);
            
            // Check if bundle options already exist (injected by Liquid or previous initialization)
            let bundleOptions = form.querySelector(".bold_option_set");
            
            if (!bundleOptions) {
                // Bundle options don't exist - inject them
                this.injectBundleOptions(productItemElement, bundleData, productId);
                bundleOptions = form.querySelector(".bold_option_set");
            }
            
            // Ensure proper initial visibility state
            const bundleChooseBtn = productItemElement.querySelector("#bundle-choose");
            const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            
            if (bundleOptions) {
                // Bundle options exist - ensure they're hidden initially
                bundleOptions.classList.add("hide-bundle-choose");
                
                // Show "Choose Size" button if it exists (for metaobject bundles)
                if (bundleChooseBtn) {
                    bundleChooseBtn.classList.remove("hide-bundle-choose");
                    bundleChooseBtn.removeAttribute("disabled");
                    // Ensure it's visible (remove any inline styles that might hide it)
                    bundleChooseBtn.style.display = '';
                }
                
                // Add required classes and hide Add to Cart button initially
                if (addToCartBtn) {
                    addToCartBtn.classList.add("bundle-product");
                    addToCartBtn.classList.add("add-to-cart");
                    addToCartBtn.classList.add("hide-bundle-choose");
                }
            }
            
        } else {
            // No bundle data - ensure buttons are enabled
            const addToCartButton = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            if (addToCartButton) addToCartButton.removeAttribute("disabled");
            const bundleChooseButton = productItemElement.querySelector("#bundle-choose");
            if (bundleChooseButton) bundleChooseButton.removeAttribute("disabled");
        }
    },
    
    /**
     * Check if a product is out of stock
     * @param {HTMLElement} productItemElement - Product item element
     * @returns {boolean} True if product is out of stock
     */
    isProductOutOfStock(productItemElement) {
        // Check for out-of-stock badge
        const soldoutBadge = productItemElement.querySelector(".productitem__badge--soldout");
        if (soldoutBadge) {
            return true;
        }
        
        // Check for "Out of stock" button with disabled attribute
        const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
        if (addToCartBtn) {
            const ariaLabel = addToCartBtn.getAttribute("aria-label");
            const isDisabled = addToCartBtn.hasAttribute("disabled") || addToCartBtn.classList.contains("disabled");
            
            if (ariaLabel === "Out of stock" && isDisabled) {
                return true;
            }
        }
        
        return false;
    },

    /**
     * Inject bundle options HTML into product form
     * @param {HTMLElement} productItemElement - Product item element
     * @param {Object} bundleData - Bundle data object
     * @param {string} parentProductId - Parent product ID
     */
    injectBundleOptions(productItemElement, bundleData, parentProductId) {
        let option_set = '<div class="bold_option_set hide-bundle-choose">';
        let optionsHtml = '';
        let name_attributes = [];
        let product_attributes = [];
        let varient_attributes = [];
        let price_attributes = [];
        let qty_attributes = [];
        let shop_product_id = parseInt(parentProductId);

        if (bundleData.bundle_options && bundleData.bundle_options[0] && bundleData.bundle_options[0].options) {
            const options_data = sortBundleOptions(bundleData.bundle_options[0].options);
            
            options_data.forEach((option, b) => {
                const required_type = (option.is_required == 1) ? "required" : "";

                if (option.options_values && option.options_values.length > 1) {
                    // Multiple variants - show as dropdown
                    const cleanedOptionName = cleanOptionName(option.bundle_option_name);
                    const handleizeName = handleize(cleanedOptionName);
                    optionsHtml += `<div class="bold_option bold_option_dropdown ${handleizeName}"><label>`;
                    optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                    optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" attr-name="${cleanedOptionName}" name="properties[${cleanedOptionName}]" data-original-name="${option.bundle_option_name}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                    optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} --</option>`;

                    option.options_values.forEach((variant, v) => {
                        if (variant.available !== false) {
                            const selected = (variant.is_default == 1) ? 'selected' : '';
                            const variantQty = (variant.qty != null && variant.qty !== '') ? String(variant.qty) : '1';
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="${v}" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" ${selected}>${cleanOptionName(variant.option_value)}</option>`;
                        }
                    });
                    optionsHtml += `</select></span></label></div>`;
                } else if (option.options_values && option.options_values.length === 1) {
                    // Single variant - create dropdown (visible or hidden based on option type)
                    const variant = option.options_values[0];
                    if (variant.available !== false) {
                        const cleanedOptionName = cleanOptionName(option.bundle_option_name);
                        const handleizeName = handleize(cleanedOptionName);
                        const required_type = (option.is_required == 1) ? "required" : "";
                        
                        // Define which single-variant options should be visible
                        const visibleSingleVariantOptions = [
                            'Piston Set', 'Piston Ring Set', 'Main Bearing Set', 'Rod Bearing Set'
                        ];
                        
                        const shouldBeVisible = visibleSingleVariantOptions.some(visibleOption => 
                            cleanedOptionName.toLowerCase().includes(visibleOption.toLowerCase())
                        );
                        
                        const variantQty = (variant.qty != null && variant.qty !== '') ? String(variant.qty) : '1';
                        if (shouldBeVisible) {
                            // Create visible dropdown for important single-variant options
                            optionsHtml += `<div class="bold_option bold_option_dropdown ${handleizeName}"><label>`;
                            optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                            optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" attr-name="${cleanedOptionName}" name="properties[${cleanedOptionName}]" data-original-name="${option.bundle_option_name}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                            optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} --</option>`;
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="0" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" selected>${cleanOptionName(variant.option_value)}</option>`;
                            optionsHtml += `</select></span></label></div>`;
                        } else {
                            // Create hidden dropdown for other single-variant options
                            optionsHtml += `<div class="bold_option bold_option_dropdown" style="display: none;"><label>`;
                            optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                            optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" style="display: none;" name="properties[${cleanedOptionName}]" data-original-name="${option.bundle_option_name}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                            optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} --</option>`;
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="0" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" selected>${cleanOptionName(variant.option_value)}</option>`;
                            optionsHtml += `</select></span></label></div>`;
                            
                            // Add to initial attributes for hidden single-variant options
                            name_attributes.push(variant.option_value);
                            product_attributes.push(variant.product_id);
                            varient_attributes.push(variant.varient_id);
                            qty_attributes.push(variant.qty || '1');
                            shop_product_id += parseInt(variant.varient_id);
                            price_attributes.push(variant.price.toString().replace(".", ""));
                        }
                    }
                }
            });
        }

        const nameAttributesStr = name_attributes.length > 0 ? name_attributes.join(",").trim() : "";
        const productAttributesStr = product_attributes.length > 0 ? product_attributes.join(",").trim() : "";
        const varientAttributesStr = varient_attributes.length > 0 ? varient_attributes.join(",").trim() : "";
        const qtyAttributesStr = qty_attributes.length > 0 ? qty_attributes.join(",").trim() : "";
        const priceAttributesStr = price_attributes.length > 0 ? price_attributes.join(",").trim().replace(".", "") : "";

        option_set += optionsHtml;
        option_set += `<div class="bold_option_total">
            <input type="hidden" name="properties[_boldVariantNames]" data-property-name="_boldVariantNames" value="${nameAttributesStr}">
            <input type="hidden" name="properties[_boldVariantIds]" data-property-name="_boldVariantIds" value="${varientAttributesStr}">
            <input type="hidden" name="properties[_boldProductIds]" data-property-name="_boldProductIds" value="${productAttributesStr}">
            <input type="hidden" name="properties[_boldVariantPrices]" data-property-name="_boldVariantPrices" value="0">
            <input type="hidden" name="properties[_boldVariantQtys]" data-property-name="_boldVariantQtys" value="${qtyAttributesStr}">
            <input type="hidden" name="properties[_boldBuilderId]" data-property-name="_boldBuilderId" value="${shop_product_id}">
        </div></div>`;

        // Find the product form and inject bundle options
        const productForm = productItemElement.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
        if (productForm) {
            // Check if bundle options already exist
            let existingBundleOptions = productForm.querySelector(".bold_option_set");
            
            if (!existingBundleOptions) {
                // Insert bundle options at the beginning of the form
                productForm.insertAdjacentHTML("afterbegin", option_set);
                
                // Get the newly inserted bundle options
                existingBundleOptions = productForm.querySelector(".bold_option_set");
            }
            
            // Ensure bundle options are hidden initially
            if (existingBundleOptions) {
                existingBundleOptions.classList.add("hide-bundle-choose");
            }

            // Enable buttons and set initial visibility
            const addToCartButton = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            if (addToCartButton) {
                addToCartButton.removeAttribute("disabled");
                // Add required classes for theme's add-to-cart handler to recognize and process this as a bundle
                addToCartButton.classList.add("bundle-product");
                addToCartButton.classList.add("add-to-cart");
                // Hide add to cart button initially
                addToCartButton.classList.add("hide-bundle-choose");
            }
            
            const bundleChooseButton = productItemElement.querySelector("#bundle-choose");
            if (bundleChooseButton) {
                bundleChooseButton.removeAttribute("disabled");
                // Show choose button initially
                bundleChooseButton.classList.remove("hide-bundle-choose");
            }
        }
    },

    /**
     * Update bundle hidden fields when options change
     * @param {HTMLElement} productForm - Product form element
     */
    updateBundleHiddenFieldsModal(productForm) {
        if (!productForm) return;

        const productItem = productForm.closest('[attr-prod-id]');
        if (!productItem) return;

        const parentBundleId = productItem.getAttribute("attr-prod-id");
        const bundleData = this.bundleCache.get(parentBundleId);

        if (!bundleData || !bundleData.bundle_options || !bundleData.bundle_options[0]) return;

        const optionsData = bundleData.bundle_options[0].options;
        let nameAttributes = [];
        let productAttributes = [];
        let varientAttributes = [];
        let qtyAttributes = [];
        let priceAttributes = [];
        let shopProductId = parseInt(parentBundleId);

        // Process visible select elements (use data-qty from Bundle Item Quantity when present)
        productForm.querySelectorAll(".bold_option_element select").forEach((selectElement, index) => {
            const selectedOption = selectElement.options[selectElement.selectedIndex];
            
            if (selectedOption && selectedOption.value) {
                const variantId = selectedOption.getAttribute('data-variant-id');
                const productId = selectedOption.getAttribute('data-product-id');
                const price = selectedOption.getAttribute('data-price');
                const qty = selectedOption.getAttribute('data-qty') || '1';
                
                if (variantId && productId) {
                    nameAttributes.push(selectedOption.value);
                    productAttributes.push(productId);
                    varientAttributes.push(variantId);
                    qtyAttributes.push(qty);
                    shopProductId += parseInt(variantId);
                    priceAttributes.push(price ? price.toString().replace(".", "") : "0");
                }
            }
        });

        // Process hidden inputs (for single variant options; use data-qty when present)
        productForm.querySelectorAll('input[type="hidden"][data-variant-id]').forEach((hiddenInput) => {
            const variantId = hiddenInput.getAttribute('data-variant-id');
            const productId = hiddenInput.getAttribute('data-product-id');
            const price = hiddenInput.getAttribute('data-price');
            const qty = hiddenInput.getAttribute('data-qty') || '1';
            
            if (variantId && productId) {
                nameAttributes.push(hiddenInput.value);
                productAttributes.push(productId);
                varientAttributes.push(variantId);
                qtyAttributes.push(qty);
                shopProductId += parseInt(variantId);
                priceAttributes.push(price ? price.toString().replace(".", "") : "0");
            }
        });

        const nameAttributesStr = nameAttributes.length > 0 ? nameAttributes.join(",").trim() : "";
        const productAttributesStr = productAttributes.length > 0 ? productAttributes.join(",").trim() : "";
        const varientAttributesStr = varientAttributes.length > 0 ? varientAttributes.join(",").trim() : "";
        const qtyAttributesStr = qtyAttributes.length > 0 ? qtyAttributes.join(",").trim() : "";
        const priceAttributesStr = priceAttributes.length > 0 ? priceAttributes.join(",").trim().replace(".", "") : "";

        const boldOptionTotal = productForm.querySelector(".bold_option_total");
        if (boldOptionTotal) {
            const totalHtml = `
                <input type="hidden" name="properties[_boldVariantNames]" data-property-name="_boldVariantNames" value="${nameAttributesStr}">
                <input type="hidden" name="properties[_boldVariantIds]" data-property-name="_boldVariantIds" value="${varientAttributesStr}">
                <input type="hidden" name="properties[_boldProductIds]" data-property-name="_boldProductIds" value="${productAttributesStr}">
                <input type="hidden" name="properties[_boldVariantPrices]" data-property-name="_boldVariantPrices" value="0">
                <input type="hidden" name="properties[_boldVariantQtys]" data-property-name="_boldVariantQtys" value="${qtyAttributesStr}">
                <input type="hidden" name="properties[_boldBuilderId]" data-property-name="_boldBuilderId" value="${shopProductId}">
            `;
            boldOptionTotal.innerHTML = totalHtml;
        }
    },

    /**
     * Validate bundle options and clear errors
     * @param {HTMLElement} productForm - Product form element
     */
    validateAndClearErrors(productForm) {
        if (!productForm) return;

        const bundleOptions = productForm.querySelector('.bold_option_set');
        if (!bundleOptions) return;

        const selects = bundleOptions.querySelectorAll('select.bundle_option_select');
        if (selects.length === 0) return;

        // Check if all required visible options are selected (ignore hidden single-variant dropdowns)
        const visibleSelects = Array.from(selects).filter(select => {
            const parentDiv = select.closest('.bold_option');
            return parentDiv && parentDiv.style.display !== 'none';
        });
        
        const allSelected = visibleSelects.every(select => {
            const allOptions = Array.from(select.querySelectorAll('option'));
            const nonEmptyOptions = allOptions.filter(option => option.value.trim() !== '');
            
            if (nonEmptyOptions.length === 1) {
                // Auto-select the single option if not already selected
                if (select.value.trim() === '') {
                    select.value = nonEmptyOptions[0].value;
                }
                return true; // Single option is considered valid
            }
            return select.value.trim() !== '';
        });

        if (allSelected) {
            // All options selected - clear any existing error messages
            const existingErrors = bundleOptions.querySelectorAll('.required-error');
            existingErrors.forEach(error => error.remove());
            
            // Remove red border styling from all visible selects
            visibleSelects.forEach(select => select.classList.remove('required-border'));
        } else {
            // Not all visible options selected - add red border to empty visible selects
            visibleSelects.forEach(select => {
                if (select.value.trim() === '') {
                    select.classList.add('required-border');
                } else {
                    select.classList.remove('required-border');
                }
            });
        }
    }
};

// Handle bundle choose button clicks
document.addEventListener("click", function (event) {
    // Check for bundle-choose button (with or without product ID suffix)
    if (event.target.matches("#bundle-choose") || event.target.closest("#bundle-choose")) {
        const bundleChooseBtn = event.target.matches("#bundle-choose") ? event.target : event.target.closest("#bundle-choose");
        if (!bundleChooseBtn) return;
        
        event.preventDefault();
        event.stopPropagation();
        
        const productItem = bundleChooseBtn.closest('[attr-prod-id]');
        if (!productItem) return;
        
        // Check if product is out of stock - don't show bundle options for out-of-stock products
        if (window.collectionBundleManager.isProductOutOfStock(productItem)) {
            console.warn(`[Collection Bundle] Cannot show bundle options for out-of-stock product`);
            return;
        }
        
        const form = productItem.querySelector("form[data-product-form], form.addToCartForm");
        if (!form) return;
        
        const productId = productItem.getAttribute("attr-prod-id");
        
        // Hide the choose button
        bundleChooseBtn.classList.add("hide-bundle-choose");
        
        // Check if bundle options exist
        let bundleOptions = form.querySelector(".bold_option_set");
        
        if (bundleOptions) {
            // Bundle options already exist, just show them
            bundleOptions.classList.remove("hide-bundle-choose");
        } else {
            // No bundle options found - check if we have metaobject data
            const bundleData = window.collectionBundleData?.[productId];
            
            if (bundleData) {
                // We have bundle data but options weren't injected yet - inject them now
                window.collectionBundleManager.injectBundleOptions(productItem, bundleData, productId);
                bundleOptions = form.querySelector(".bold_option_set");
                if (bundleOptions) {
                    bundleOptions.classList.remove("hide-bundle-choose");
                }
            } else {
                console.warn(`No metaobject bundle data found for product ${productId}. Choose button should not be shown.`);
                
                // Show add to cart button directly
                const addToCartBtn = productItem.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
                if (addToCartBtn) {
                    addToCartBtn.classList.remove("hide-bundle-choose");
                }
            }
        }
        
        // Show add to cart button and ensure it has required classes for theme handler
        const addToCartBtn = productItem.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
        if (addToCartBtn) {
            addToCartBtn.classList.add("bundle-product");
            addToCartBtn.classList.add("add-to-cart");
            addToCartBtn.classList.remove("hide-bundle-choose");
        }
        
        // Update bundle hidden fields with current selections (if bundle options exist)
        setTimeout(() => {
            if (form.querySelector(".bold_option_element select")) {
                window.collectionBundleManager.updateBundleHiddenFieldsModal(form);
            }
        }, 100);
    }
});

// Handle bundle option changes
document.addEventListener("change", (event) => {
    if (event.target.matches(".bundle_option_select")) {
        // Stop event propagation to prevent YMM handler from running
        event.stopPropagation();
        
        const productForm = event.target.closest("form");
        if (productForm) {
            const productItem = productForm.closest('[attr-prod-id]');
            const productId = productItem ? productItem.getAttribute("attr-prod-id") : null;
            
            // Double-check this is a metaobject bundle
            if (productId && window.collectionBundleData && window.collectionBundleData[productId]) {
                const attrElement = event.target;
                const attrName = attrElement.getAttribute("attr-name");
                
                // Special handling for Piston Set -> Piston Ring Set (auto-suggestion)
                if (attrName === "Piston Set") {
                    let pistonSet = attrElement.value;
                    
                    if (pistonSet && pistonSet !== "") {
                        const pistonRingSetElement = productForm.querySelector('.bold_option_element select[attr-name="Piston Ring Set"]');
                        if (pistonRingSetElement) {
                            // Convert "Piston Set" option to corresponding "Piston Ring Set" option
                            let pistonRingSet = pistonSet.replace("Piston Set", "Piston Ring Set");
                            
                            // Check if the corresponding option exists in Piston Ring Set dropdown
                            const optionExists = Array.from(pistonRingSetElement.options).some(opt => opt.value === pistonRingSet);
                            if (optionExists) {
                                // Only update if it's different from current selection
                                if (pistonRingSetElement.value !== pistonRingSet) {
                                    pistonRingSetElement.value = pistonRingSet;
                                    
                                    // Add a visual indicator that this was auto-selected
                                    pistonRingSetElement.style.backgroundColor = '#f0f8ff';
                                    setTimeout(() => {
                                        pistonRingSetElement.style.backgroundColor = '';
                                    }, 2000);
                                    
                                    // Trigger change event to update hidden fields (don't bubble to prevent YMM handler)
                                    pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                                }
                            } else {
                                // Reset Piston Ring Set to empty if no matching option exists
                                if (pistonRingSetElement.value !== "") {
                                    pistonRingSetElement.value = "";
                                    pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                                }
                            }
                        }
                    } else {
                        // If Piston Set is cleared, also clear Piston Ring Set
                        const pistonRingSetElement = productForm.querySelector('.bold_option_element select[attr-name="Piston Ring Set"]');
                        if (pistonRingSetElement && pistonRingSetElement.value !== "") {
                            pistonRingSetElement.value = "";
                            pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                        }
                    }
                }
                
                window.collectionBundleManager.updateBundleHiddenFieldsModal(productForm);
                
                // Clear error messages when user makes selections
                window.collectionBundleManager.validateAndClearErrors(productForm);
            } else {
                // Not a metaobject bundle - let YMM handler process it
                return;
            }
        }
    }
});

// Handle add to cart for metaobject bundles
document.addEventListener("click", async function (event) {
    // Check if clicked element is add to cart button
    const addToCartBtn = event.target.closest(".productitem--action-atc, [data-quick-buy]");
    if (!addToCartBtn) return;
    
    // Find the product item - look for the <li> or parent container, not the button itself
    let productItem = addToCartBtn.closest('.productgrid--item');
    if (!productItem) productItem = addToCartBtn.closest('[attr-prod-id]');
    if (!productItem) return;
    
    const productId = productItem.getAttribute('attr-prod-id');
    
    // Check if this is a metaobject bundle
    if (!window.collectionBundleData || !window.collectionBundleData[productId]) {
        // Not a metaobject bundle - let normal handler process
        return;
    }
    
    // This is a metaobject bundle - handle it
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Try multiple ways to find the form
    let form = productItem.querySelector("form[data-product-form]");
    if (!form) form = productItem.querySelector("form.addToCartForm");
    if (!form) form = productItem.querySelector("form.js-product-form");
    if (!form) form = productItem.querySelector("form");
    
    if (!form) {
        console.error('[Collection Bundle] Form not found in product item:', productItem);
        console.error('[Collection Bundle] Product item HTML:', productItem.outerHTML.substring(0, 500));
        return false;
    }
    
    const bundleOptions = form.querySelector('.bold_option_set');
    
    // If bundle options exist and are visible, validate them
    if (bundleOptions && !bundleOptions.classList.contains('hide-bundle-choose')) {
        const selects = bundleOptions.querySelectorAll('select.bundle_option_select');
        const visibleSelects = Array.from(selects).filter(select => {
            const parentDiv = select.closest('.bold_option');
            return parentDiv && parentDiv.style.display !== 'none';
        });
        
        // Clear existing errors
        const existingErrors = bundleOptions.querySelectorAll('.required-error');
        existingErrors.forEach(error => error.remove());
        
        // Validate
        let hasErrors = false;
        visibleSelects.forEach(select => {
            select.classList.remove('required-border');
            // Check if this select has options other than the placeholder
            const nonEmptyOptions = Array.from(select.options).filter(opt => opt.value.trim() !== '');
            
            if (nonEmptyOptions.length > 0 && select.value.trim() === '') {
                select.classList.add('required-border');
                hasErrors = true;
            }
        });
        
        if (hasErrors) {
            // Show error message
            const errorMessage = document.createElement('p');
            errorMessage.classList.add('required-error');
            errorMessage.textContent = 'Please select all required options';
            errorMessage.style.color = 'red';
            errorMessage.style.marginBottom = '10px';
            bundleOptions.insertBefore(errorMessage, bundleOptions.firstChild);
            
            console.warn('[Collection Bundle] Validation failed - missing required options');
            return false;
        }
    }
    
    // Use the new optimized cart addition
    if (window.CollectionBundleCart) {
        await window.CollectionBundleCart.addBundleToCart(form, addToCartBtn);
    } else {
        console.error('[Collection Bundle] CollectionBundleCart not found - falling back');
        // Fallback: submit form normally
        form.submit();
    }
    
    return false;
}, true); // Use capture phase to intercept before other handlers

// Initialize bundle products
function initializeCollectionBundles() {
    const collectionContainer = document.querySelector('.productgrid--outer, .productgrid--items, [data-productgrid-items], #collection-alternative');
    
    if (collectionContainer) {
        // Detect page type to use correct context
        let context = 'collection';
        if (document.body.classList.contains('template-search')) {
            context = 'search';
        }
        window.collectionBundleManager.initializeCollectionProducts(collectionContainer, context);
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initializeCollectionBundles, 100);
});

// Also initialize if DOM is already loaded
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(initializeCollectionBundles, 100);
}

// Force initialization after a short delay to ensure all scripts are loaded
setTimeout(() => {
    initializeCollectionBundles();
}, 500);

// Re-initialize when products are dynamically loaded (for AJAX pagination/filtering)
if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.addedNodes.length) {
                // Check if new product items were added
                const hasNewProducts = Array.from(mutation.addedNodes).some(node => {
                    if (node.nodeType === 1) { // Element node
                        return node.matches && (
                            node.matches('[attr-prod-id]') ||
                            node.querySelector('[attr-prod-id]')
                        );
                    }
                    return false;
                });
                
                if (hasNewProducts) {
                    setTimeout(initializeCollectionBundles, 200);
                }
            }
        });
    });
    
    // Observe the collection container for changes
    document.addEventListener('DOMContentLoaded', () => {
        const collectionContainer = document.querySelector('.productgrid--outer, .productgrid--items, [data-productgrid-items], #collection-alternative');
        if (collectionContainer) {
            observer.observe(collectionContainer, {
                childList: true,
                subtree: true
            });
        }
    });
}

// Also listen for custom events that might indicate products were loaded
document.addEventListener('productgrid:updated', initializeCollectionBundles);
document.addEventListener('collection:products-loaded', initializeCollectionBundles);
